import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowRight, XCircle } from "lucide-react";
import Seo from "@/components/Seo";
import { useCart } from "@/context/CartContext";
import { api, fmt } from "@/lib/api";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const paypalOrderId = params.get("order_id");
  const provider = params.get("provider");
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const { clearCart } = useCart();

  useEffect(() => {
    if (provider === "paypal" && paypalOrderId) {
      api
        .get(`/orders/${paypalOrderId}`)
        .then(({ data }) => {
          setOrder(data);
          setStatus("paid");
          clearCart();
        })
        .catch(() => setStatus("error"));
      return;
    }
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const timer = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          clearInterval(timer);
          setStatus("paid");
          clearCart();
          const res = await api.get(`/orders/by-session/${sessionId}`);
          setOrder(res.data);
        }
      } catch {
        // retry
      }
      if (attempts >= 15) {
        clearInterval(timer);
        setStatus((s) => (s === "paid" ? s : "error"));
      }
    }, 2000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, paypalOrderId, provider]);

  return (
    <div className="min-h-screen px-4 sm:px-6 lg:px-12 pt-40 pb-24 flex flex-col items-center text-center" data-testid="payment-success-page">
      <Seo title="Commande confirmée — S'NO" siteName="S'NO" />

      {status === "checking" && (
        <div className="flex flex-col items-center gap-6" data-testid="payment-checking">
          <Loader2 className="w-10 h-10 animate-spin text-ice" />
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">
            Vérification du paiement…
          </p>
        </div>
      )}

      {status === "paid" && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-lg w-full"
        >
          <div className="w-16 h-16 mx-auto flex items-center justify-center bg-ice text-black mb-8 shadow-[0_0_40px_rgba(0,240,255,0.4)]">
            <Check className="w-8 h-8" />
          </div>
          <h1 className="font-display font-black uppercase text-5xl sm:text-6xl leading-[0.9] mb-4" data-testid="payment-success-title">
            Commande confirmée
          </h1>
          <p className="text-sm text-zinc-400 leading-relaxed mb-8">
            Merci rider. Ta commande est payée et part en préparation.
            Tu recevras le suivi par email.
          </p>
          {order && (
            <div className="border border-white/10 bg-smoke text-left p-6 mb-8" data-testid="order-summary">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
                Commande {order.order_id.slice(0, 8)}
              </p>
              <ul className="space-y-2 mb-4">
                {order.items.map((item, i) => (
                  <li key={i} className="flex justify-between text-sm text-zinc-300">
                    <span>
                      {item.name} <span className="text-zinc-600 font-mono text-xs">/ {item.size} / {item.color} × {item.quantity}</span>
                    </span>
                    <span className="font-mono">{fmt(item.price * item.quantity)}</span>
                  </li>
                ))}
              </ul>
              <div className="flex justify-between border-t border-white/10 pt-3">
                <span className="font-mono text-xs uppercase tracking-widest text-zinc-400">Total</span>
                <span className="font-display text-xl text-ice" data-testid="order-total">{fmt(order.total)}</span>
              </div>
            </div>
          )}
          <Link to="/boutique" className="btn-ice" data-testid="back-to-shop-button">
            Retour à la boutique <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      )}

      {status === "error" && (
        <div className="flex flex-col items-center gap-6" data-testid="payment-error">
          <XCircle className="w-12 h-12 text-zinc-500" />
          <p className="font-display uppercase text-3xl text-zinc-400">
            Vérification impossible
          </p>
          <p className="text-sm text-zinc-500 max-w-sm">
            Si ton paiement est passé, ta commande est bien enregistrée. Écris-nous sur WhatsApp avec ta confirmation.
          </p>
          <Link to="/" className="btn-ghost">Retour à l'accueil</Link>
        </div>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen px-4 sm:px-6 lg:px-12 pt-40 pb-24 flex flex-col items-center text-center" data-testid="payment-cancel-page">
      <Seo title="Paiement annulé — S'NO" siteName="S'NO" />
      <XCircle className="w-12 h-12 text-zinc-500 mb-8" />
      <h1 className="font-display font-black uppercase text-5xl sm:text-6xl leading-[0.9] mb-4">
        Paiement annulé
      </h1>
      <p className="text-sm text-zinc-400 leading-relaxed mb-10 max-w-md">
        Aucun montant n'a été débité. Ton panier est toujours là, prêt quand tu l'es.
      </p>
      <Link to="/boutique" className="btn-ice" data-testid="cancel-back-to-shop-button">
        Retour à la boutique <ArrowRight className="w-5 h-5" />
      </Link>
    </div>
  );
}
